import AddCircle from '@betfan/icons/linear/EssentionalUI/AddCircle.svg?react';
import cn from 'classnames';
import { CouponRadioButton } from 'features/coupon/component/CouponRadioButton/CouponRadioButton';
import FreeBetSelection from 'features/coupon/component/FreeBetSelection';
import { handleBalanceData } from 'features/customerPanel/store/cpPanel.action';
import { OffersState } from 'features/offers/interface/store.interface';
import { activateOfferAction, fetchBonusesAndFreebets } from 'features/offers/store/offers.action';
import { RewardCard } from 'features/RewardCard';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Button from 'shared/components/form/button';
import { addNotification } from 'shared/components/toast';
import { BONUS_BALANCE, defaultBreakpoints, FREEBET, MAIN_BALANCE } from 'shared/constants/common';
import { FINANCIAL_THRESHOLDS } from 'shared/constants/financial-thresholds';
import { useDeposit } from 'shared/hooks/useDeposit';
import { BalanceState, BETSType, State } from 'shared/interface';
import { addBet } from 'shared/store/coupon.action';
import { getRemainingFreebetBalance, getUpdatedBalanceData } from 'shared/util/utility';
import { Mousewheel, SwiperOptions } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import './styles.scss';

interface BetslipContentProps {
	bets: BETSType;
	deselectFreeBet: () => void;
	isFreebetsActiveEmpty: boolean;
	isBonusesQueuedEmpty: boolean;
	isBonusesActiveEmpty: boolean;
	bonusesActive: OffersState['bonusesActive'];
	bonusesQueued: OffersState['bonusesQueued'];
	freebetsActive: OffersState['freebetsActive'];
	isFreebet: boolean;
	freeBets: any[];
	handelSelectedFreeBet: (id: string, stack: number) => void;
	selectedFreeBet: string;
	isSystemBetTab: boolean;
	handleBottomDrawer: () => void;
}
export const BetslipContent: React.FC<BetslipContentProps> = ({
	bets,
	bonusesActive,
	bonusesQueued,
	freebetsActive,
	isFreebetsActiveEmpty,
	isBonusesQueuedEmpty,
	isBonusesActiveEmpty,
	isFreebet,
	handelSelectedFreeBet,
	selectedFreeBet,
	deselectFreeBet,
	isSystemBetTab,
	handleBottomDrawer,
}) => {
	const balanceData = useSelector<State, BalanceState>((state) => state.cpPanel.balanceData);

	const { mainBalance, selectedBalanceType } = balanceData;
	const { openDepositPanel } = useDeposit();

	const { bonusBet, isFreeBetSelected } = bets;

	const { t } = useTranslation();
	const dispatch = useDispatch();
	const isMainBalanceEmpty = mainBalance === 0;
	const depositBalanceBelowTwo = !!mainBalance && mainBalance < FINANCIAL_THRESHOLDS.MAX_BALANCE_FOR_QUICK_TOP_UP && mainBalance > 0;

	const breakpoints = Object.entries(defaultBreakpoints).reduce((acc, [key, value]) => {
		acc[Number(key)] = {
			slidesPerView: bonusesQueued.length === 1 ? 1 : value.slidesPerView,
		};
		return acc;
	}, {} as Record<number, { slidesPerView: number | 'auto' }>);

	const settings: SwiperOptions = {
		slidesPerView: bonusesQueued.length === 1 ? 1 : 'auto',
		modules: [Mousewheel],
		allowTouchMove: true,
		grabCursor: true,
		loop: false,
		threshold: 5,
		spaceBetween: 8,
		preventClicks: true,
		breakpoints,
	};

	const [balanceTypeRadio, setBalanceTypeRadio] = useState('');
	const handleRadioChange = (id: string) => {
		if (id === MAIN_BALANCE) {
			dispatch(handleBalanceData({ ...balanceData, selectedBalanceType: id }));
			dispatch(addBet({ ...bets, bonusBet: false, isFreeBetSelected: false, freebetId: null, freeBetStack: null }));
			handleBottomDrawer();
		}

		deselectFreeBet();
		setBalanceTypeRadio(id);
	};

	const getRemainingBonusBalance = (bonusesActive: OffersState['bonusesActive'], bonusesQueued: OffersState['bonusesQueued']) => {
		const activeBonus = bonusesActive.reduce((acc, bonus) => acc + (bonus.balanceValue?.amount || 0), 0);
		const queuedBonus = bonusesQueued.reduce((acc, bonus) => acc + (bonus.bonusValue || 0), 0);
		return activeBonus + queuedBonus;
	};

	const depositBalanceChecked = balanceTypeRadio === MAIN_BALANCE;
	const bonusBalanceChecked = balanceTypeRadio === BONUS_BALANCE;
	const freebetChecked = balanceTypeRadio === FREEBET;

	const radioButtons = [
		{
			name: 'betslipBalanceType',
			value: MAIN_BALANCE,
			id: MAIN_BALANCE,
			onChange: () => {
				handleRadioChange(MAIN_BALANCE);
			},
			checked: depositBalanceChecked,
			cyTestRef: 'betslip-mainbalance',
			balance: mainBalance,
			depositBalanceBelowTwo: depositBalanceBelowTwo,
		},
		{
			name: 'betslipBalanceType',
			value: BONUS_BALANCE,
			id: BONUS_BALANCE,
			dotNumber: bonusesActive.length + bonusesQueued.length,
			onChange: () => handleRadioChange(BONUS_BALANCE),
			checked: (bonusBalanceChecked || selectedBalanceType === BONUS_BALANCE) && !freebetChecked,
			disabled: (isBonusesQueuedEmpty && isBonusesActiveEmpty) || isSystemBetTab,
			cyTestRef: 'betslip-bonusbalance',
			balance: getRemainingBonusBalance(bonusesActive, bonusesQueued),
			depositBalanceBelowTwo: false,
		},
		{
			name: 'betslipBalanceType',
			value: FREEBET,
			id: FREEBET,
			dotNumber: freebetsActive.length,
			onChange: () => handleRadioChange(FREEBET),
			checked: freebetChecked,
			disabled: isFreebetsActiveEmpty || isSystemBetTab,
			cyTestRef: 'betslip-freebet',
			balance: getRemainingFreebetBalance(freebetsActive),
			depositBalanceBelowTwo: false,
		},
	];

	const activateOffer = async (offerId: number) => {
		try {
			dispatch(activateOfferAction(offerId));

			const latestBalanceData = await getUpdatedBalanceData();
			dispatch(handleBalanceData({ ...latestBalanceData, selectedBalanceType: MAIN_BALANCE }));
			addNotification('success', `${t('bonusActivatedMessage')}!`);
		} catch (error) {
			addNotification('error', `${t('genericErrorMessage')}!`);
		}
	};
	const determineBalanceType = () => {
		if (isFreeBetSelected) return FREEBET;
		if (selectedBalanceType === BONUS_BALANCE && bonusBet) return BONUS_BALANCE;
		return MAIN_BALANCE;
	};

	useEffect(() => {
		setBalanceTypeRadio(determineBalanceType());
	}, [selectedBalanceType, isFreeBetSelected]);

	useEffect(() => {
		if (balanceTypeRadio === BONUS_BALANCE) {
			dispatch(fetchBonusesAndFreebets());
		}
	}, [balanceTypeRadio]);

	const activateFreebet = (freebetId: number, freebetStake: number) => {
		handelSelectedFreeBet(freebetId.toString(), freebetStake);
		handleBottomDrawer();
	};

	return (
		<div>
			<p className="font-bold text-[20px] m-0 text-[var(--event-card-color)] pb-3 leading-[28px] tracking-[-0.02em]">{t('whatToPlayFor')}</p>
			<div className="mt-1 mb-3 flex flex-row w-full items-center justify-between gap-3">
				{radioButtons.map((item, index: number) => (
					<CouponRadioButton
						key={`couponRadioButton--${index}`}
						{...item}
						cyTestRef={item.cyTestRef}
						depositBalanceBelowTwo={depositBalanceBelowTwo}
						mainBalance={item.balance || 0}
					/>
				))}
			</div>

			<div className="betslipContent__content__deposits relative overflow-y-auto overflow-x-hidden">
				<div
					className={cn('betslipContent__content--deposit', {
						'betslipContent__content--deposit--visible': depositBalanceChecked,
					})}
				>
					<>
						{isMainBalanceEmpty && (
							<span className="text-paragraph-md font-medium text-[var(--event-card-color)]">
								{t('main_balance_empty')} {t('time_to_top_up')}
							</span>
						)}
						{depositBalanceBelowTwo && (
							<span className="text-paragraph-md font-medium text-[var(--event-card-color)]">
								<span className="font-bold">{t('deposit_balance_below_two')}</span> {t('time_to_top_up')}
							</span>
						)}
						{(isMainBalanceEmpty || depositBalanceBelowTwo) && (
							<Button className="h-16 flex items-center justify-center" onClick={() => openDepositPanel()}>
								<div className="flex flex-row items-center gap-1">
									<div className="flex items-center">
										<AddCircle className="text-primary-400 size-5" />
									</div>
									<div className="flex items-center text-paragraph-lg">{t('deposit')}</div>
								</div>
							</Button>
						)}
					</>
				</div>

				<div
					className={cn('betslipContent__content--bonus', {
						'betslipContent__content--deposit--visible': bonusBalanceChecked,
					})}
				>
					{bonusesActive.length > 0 && (
						<>
							<div className="betslipContent__content__activeBonus">
								{bonusesActive.map((bonus, index) => (
									<RewardCard data={bonus} key={`rewardCard-${index}`} handleBottomDrawer={handleBottomDrawer} />
								))}
							</div>
						</>
					)}

					{bonusesQueued.length > 0 && (
						<>
							<div className="text-paragraph-sm text-primary-500 dark:text-gray-100 font-medium pb-2">{t('awaitingBonus')}</div>
							<Swiper {...settings} freeMode={true} navigation={true}>
								{bonusesQueued.map((quequedBonus, index) => {
									return (
										<SwiperSlide key={`bannerData-${index}`}>
											<RewardCard activateOffer={activateOffer} bonusesActive={bonusesActive} data={quequedBonus} />
										</SwiperSlide>
									);
								})}
							</Swiper>
						</>
					)}
				</div>
				<div
					className={cn('betslipContent__content--freebet', {
						'betslipContent__content--freebet--visible': freebetChecked,
					})}
				>
					<FreeBetSelection freeBets={freebetsActive} activateFreebet={activateFreebet} selectedFreeBet={selectedFreeBet} />
				</div>
			</div>
			{isFreebet}
		</div>
	);
};
